// Season editor form sections: General, Dates, Irrigation system, Contract end
// date, Advance (collapsed, not implemented). Values may be MULTIPLE_VALUES in
// bulk edit; option lists come from the same Redux dictionaries the V1
// UpdateFieldModal / KCBox use (soil_data, crop_data, farming_type_data).
const React = require('react');
const { useMemo } = require('react');
const { useSelector } = require('react-redux');
const { useTranslation } = require('react-i18next');
const dayjs = require('../../utils/dayjsConfig');
const { upper } = require('helpers');
const { unitDisplayName } = require('unitsManager');
const { MULTIPLE_VALUES } = require('./seasonHelpers');
const { IRRIGATION_TYPE_OPTIONS } = require('./newSeasonConstants');

const DATE_INPUT_FORMAT = 'YYYY-MM-DD';

const isMultiple = (value) => value === MULTIPLE_VALUES;

const toDateInput = (value, isUnix) => {
  if (!value || isMultiple(value)) {
    return '';
  }
  const parsed = isUnix ? dayjs.unix(value) : dayjs(value);
  return parsed.isValid() ? parsed.format(DATE_INPUT_FORMAT) : '';
};

// `fixedWidth` keeps a section's inputs at a fixed 400px (the form fields);
// without it the body fills the row (the Map).
const Section = ({ title, fixedWidth = false, children }) => (
  <div className='ns-section'>
    <div className='ns-section-title'>{title}</div>
    <div
      className={
        'ns-section-body' + (fixedWidth ? ' ns-section-body-fixed' : '')
      }
    >
      {children}
    </div>
  </div>
);

const Row = ({ label, children }) => (
  <div className='ns-form-row'>
    <label className='ns-form-label'>{label}</label>
    <div className='ns-form-input'>{children}</div>
  </div>
);

// options: [{value, label}]. A current value missing from the list is kept as
// an option so the select never silently shows a different value.
const OptionSelect = ({ value, options, onChange, disabled }) => {
  const { t } = useTranslation();
  const hasValue =
    value === '' || isMultiple(value) || options.some((o) => o.value === value);
  return (
    <select
      className='form-control custom-select ns-input'
      value={value ?? ''}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    >
      {isMultiple(value) && (
        <option value={MULTIPLE_VALUES}>
          {t('new_season_multiple_values')}
        </option>
      )}
      <option value=''>{t('new_season_select')}</option>
      {!hasValue && <option value={value}>{value}</option>}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

const keysAsOptions = (object, t) =>
  Object.keys(object || {})
    .sort()
    .map((key) => ({ value: key, label: t(upper(key)) }));

const useOptionDictionaries = () => {
  const soilData = useSelector(
    (state) => state.soil_data?.soil_data?.soil_data,
  );
  const cropData = useSelector(
    (state) => state.crop_data?.crop_data?.crop_data,
  );
  const farmingTypes = useSelector(
    (state) => state.farming_type_data?.farming_type_data,
  );
  return { soilData, cropData, farmingTypes };
};

const soilOptions = (soilData, t) =>
  Object.keys(soilData || {})
    .filter((key) => soilData[key].active === true)
    .sort()
    .map((key) => ({ value: key, label: t(upper(key)) }));

// Stored soil types may differ in case from the dictionary keys (V1 compares
// lower-cased); show the matching key.
const matchSoilKey = (value, options) => {
  if (!value || isMultiple(value)) {
    return value;
  }
  const match = options.find(
    (option) => option.value.toLowerCase() === String(value).toLowerCase(),
  );
  return match ? match.value : value;
};

const firstKey = (object) => Object.keys(object || {})[0] ?? '';

const GeneralSection = ({ values, onChange, onChangeMany }) => {
  const { t } = useTranslation();
  const { soilData, cropData, farmingTypes } = useOptionDictionaries();
  const soils = useMemo(() => soilOptions(soilData, t), [soilData, t]);
  const varieties = cropData?.[values.crop_type];
  const features = varieties?.[values.crop_variety];
  const farmingTypeOptions = Object.keys(farmingTypes || {}).map((key) => ({
    value: key,
    label: farmingTypes[key],
  }));
  // Same cascade as V1 KCBox: a new crop type picks its first variety and
  // feature; a new variety picks its first feature.
  const onCropType = (cropType) => {
    const variety = firstKey(cropData?.[cropType]);
    onChangeMany({
      crop_type: cropType,
      crop_variety: variety,
      crop_feature: firstKey(cropData?.[cropType]?.[variety]),
    });
  };
  const onVariety = (variety) =>
    onChangeMany({
      crop_variety: variety,
      crop_feature: firstKey(varieties?.[variety]),
    });

  return (
    <Section title={t('new_season_section_general')} fixedWidth>
      <Row label={t('soil_type')}>
        <OptionSelect
          value={matchSoilKey(values.soil_type, soils)}
          options={soils}
          onChange={(v) => onChange('soil_type', v)}
        />
      </Row>
      <Row label={t('crop_type')}>
        <OptionSelect
          value={values.crop_type}
          options={keysAsOptions(cropData, t)}
          onChange={onCropType}
        />
      </Row>
      <Row label={t('kc_variety')}>
        <OptionSelect
          value={values.crop_variety}
          options={keysAsOptions(varieties, t)}
          disabled={isMultiple(values.crop_type)}
          onChange={onVariety}
        />
      </Row>
      <Row label={t('kc_feature')}>
        <OptionSelect
          value={values.crop_feature}
          options={keysAsOptions(features, (key) => key)}
          disabled={
            isMultiple(values.crop_type) || isMultiple(values.crop_variety)
          }
          onChange={(v) => onChange('crop_feature', v)}
        />
      </Row>
      <Row label={t('farming_type')}>
        <OptionSelect
          value={values.farming_type}
          options={farmingTypeOptions}
          onChange={(v) => onChange('farming_type', v)}
        />
      </Row>
    </Section>
  );
};

const DateInput = ({ value, isUnix, onChange }) => {
  const { t } = useTranslation();
  return (
    <input
      type='date'
      className='form-control ns-input'
      value={toDateInput(value, isUnix)}
      placeholder={isMultiple(value) ? t('new_season_multiple_values') : ''}
      title={isMultiple(value) ? t('new_season_multiple_values') : undefined}
      onChange={(event) => {
        const text = event.target.value;
        if (!text) {
          onChange(isUnix ? null : '');
          return;
        }
        onChange(
          isUnix ? dayjs(text).unix() : dayjs(text).format(DATE_INPUT_FORMAT),
        );
      }}
    />
  );
};

const DatesSection = ({ values, onChange }) => {
  const { t } = useTranslation();
  return (
    <Section title={t('new_season_section_dates')} fixedWidth>
      <Row
        label={
          <span>
            {t('new_season_sow')}{' '}
            <span className='ns-info' title={t('new_season_sow_info')}>
              ⓘ
            </span>
          </span>
        }
      >
        <DateInput
          value={values.sow_date}
          onChange={(v) => onChange('sow_date', v)}
        />
        {isMultiple(values.sow_date) && (
          <div className='ns-muted'>{t('new_season_multiple_values')}</div>
        )}
      </Row>
    </Section>
  );
};

const irrigationOptions = IRRIGATION_TYPE_OPTIONS.map((option) => ({
  value: option.value,
  label: option.title,
}));

const IrrigationSection = ({ values, onChange }) => {
  const { t } = useTranslation();
  const userData = useSelector((state) => state.authentication_data?.user_data);
  const unit = userData?.length ? unitDisplayName(userData.length) : '';
  const rate = values.irrigation_rate;
  return (
    <Section title={t('new_season_section_irrigation')} fixedWidth>
      <Row label={t('irrigation_type')}>
        <OptionSelect
          value={values.irrigation_type}
          options={irrigationOptions}
          onChange={(v) => onChange('irrigation_type', v)}
        />
      </Row>
      <Row label={t('new_season_irrigation_rate_label', { unit })}>
        <input
          type='number'
          step='any'
          className='form-control ns-input'
          value={isMultiple(rate) ? '' : (rate ?? '')}
          placeholder={isMultiple(rate) ? t('new_season_multiple_values') : ''}
          onChange={(event) => {
            const parsed = parseFloat(event.target.value);
            onChange('irrigation_rate', isNaN(parsed) ? 0 : parsed);
          }}
        />
      </Row>
    </Section>
  );
};

const ContractSection = ({ values, onChange }) => {
  const { t } = useTranslation();
  return (
    <Section title={t('new_season_section_contract_end')} fixedWidth>
      <Row label={t('edit_field_contract_duration')}>
        <DateInput
          value={values.expiration_date}
          isUnix
          onChange={(v) => onChange('expiration_date', v)}
        />
        {isMultiple(values.expiration_date) && (
          <div className='ns-muted'>{t('new_season_multiple_values')}</div>
        )}
      </Row>
    </Section>
  );
};

const AdvanceSection = () => {
  const { t } = useTranslation();
  return (
    <div className='ns-section ns-disabled' aria-disabled='true'>
      <div className='ns-section-title'>
        › {t('new_season_section_advance')}
      </div>
    </div>
  );
};

module.exports = {
  Section,
  GeneralSection,
  DatesSection,
  IrrigationSection,
  ContractSection,
  AdvanceSection,
};
